import * as React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import debounce from 'lodash/debounce';
import lowerCase from 'lodash/lowerCase';

import { useScanReactSelect } from 'root/scan/useScanReactSelect';

import Autocomplete from '..';
import GuiDefHiddenInput from '../GuiDefHiddenInput';
import {
  reinstateSerialOnTransactionLine,
  removeLineItemSerializedInfoFromStorage,
  searchSerials,
  SerialsAutocompleteOption,
} from './utils';

let hasPendingSubmit = false;
const ENFORCE_SHIPPING_SERIAL_NUMBER_MODULE = 'shipping.enforce_serial_numbers';
const COMPLETE_SALE_WITHOUT_SERIAL_NUMBER_RIGHT: string =
  'register_complete_a_sale_without_selecting_a_serial_number';

const SerialsAutocomplete: React.FC<AutocompleteProps> = ({
  isAsync = true,
  defaultValue,
  placeholder = 'Scan or type a serial number',
  hiddenInput,
  defaultOptions,
  hasSubmitFormButton,
  onChange,
  inputId: _inputId,
  ...rest
}) => {
  const [value, setValue] = React.useState(defaultValue);
  const [isDisabled, setIsDisabled] = React.useState(rest.isDisabled || false);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [options, setOptions] = React.useState<string[]>(defaultOptions || []);
  const [menuIsOpen, setMenuIsOpen] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const isLoadingRef = React.useRef(isLoading);
  React.useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const submitRef = React.useRef<HTMLButtonElement>(null);
  const enforceSerialNumbers = window.lsCloud.Modules.hasModule(
    ENFORCE_SHIPPING_SERIAL_NUMBER_MODULE
  );
  const canCompleteSaleWithoutSerialNumber = window.merchantos.session.hasRight(
    COMPLETE_SALE_WITHOUT_SERIAL_NUMBER_RIGHT
  );
  const canReinstateSerialNumbers =
    enforceSerialNumbers === true && canCompleteSaleWithoutSerialNumber === false;

  const serializedItemID = rest.itemId;

  const isSerializedItemOnWorkorder = !!rest.workorderItemID;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (isLoadingRef.current) {
        e.preventDefault();
        hasPendingSubmit = true;
      }
    }
  };
  const onInputChange = (value: string) => {
    setInputValue(value);
    setMenuIsOpen(true);
    if (!isAsync) return;
    if (!value) {
      setOptions(defaultOptions || []);
      // ignore ongoing requests results and use defaultOptions
      if (isLoading) debouncedLoadSuggestions('', () => setOptions(defaultOptions || []));
      return;
    }
    // We need to set isLoadingRef to true to prevent the user from submitting the form by pressing Enter
    isLoadingRef.current = true;
    setIsLoading(true);
    debouncedLoadSuggestions(value, (res) => setOptions(value ? res : defaultOptions || []));
  };
  const inputId = useScanReactSelect({ onKeyDown, onInputChange, inputId: _inputId });

  const loadSuggestions = (query: string, callback: (args: any) => any) => {
    setIsLoading(true);
    searchSerials({ query, itemId: serializedItemID.toString() }).then((res) => {
      setIsLoading(false);
      if (
        hasPendingSubmit &&
        query.length &&
        res.length === 1 &&
        lowerCase(res[0].serial) === lowerCase(query)
      ) {
        setValue(res[0]);
      }
      return callback(res);
    });
  };

  // Make sure the fn is cached so the debounce is correctly working else a new function will
  // be created when the component updates and the previous function won't be debounced
  const debouncedLoadSuggestions = React.useCallback(debounce(loadSuggestions, 300), []);

  const { value: hiddenInputValue = '' } = value || {};

  React.useEffect(() => {
    // A part of avoiding requiring two reloads to reinstate the serial number to the line item if the form's data has changed.
    // @ts-ignore
    if (window.oldConfirm !== undefined) {
      // @ts-ignore
      window.confirm = window.oldConfirm;
    }
  }, []);

  React.useEffect(() => {
    if (value) {
      setIsDisabled(true);

      if (hasSubmitFormButton) {
        submitRef.current?.click();
      } else if (onChange === 'listingAssignItemSerial') {
        window.listingAssignItemSerial(
          rest.panelId,
          rest.transactionLineId,
          rest.customerId,
          value.value,
          false
        );

        if (canReinstateSerialNumbers) {
          removeLineItemSerializedInfoFromStorage(rest.transactionLineId);
        }
      } else if (isSerializedItemOnWorkorder) {
        window.merchantos.edit_workorder.addSerialized(rest.workorderItemID, value.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    function reinstateHandler() {
      if (canReinstateSerialNumbers) {
        reinstateSerialOnTransactionLine(
          rest.transactionLineId as number,
          rest.panelId as string,
          rest.customerId as string
        );
      }
    }

    // page reload/refresh
    window.addEventListener('beforeunload', reinstateHandler);

    // page close
    window.addEventListener('pagehide', reinstateHandler);

    // anything else (redirect, any kind of component dismount, etc.)
    return () => {
      reinstateHandler();

      window.removeEventListener('beforeunload', reinstateHandler);
      window.removeEventListener('pagehide', reinstateHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Autocomplete
        {...rest}
        inputId={inputId}
        value={value}
        inputValue={inputValue}
        defaultOptions={defaultOptions}
        options={options}
        isLoading={isLoading}
        placeholder={placeholder}
        menuIsOpen={menuIsOpen && !isDisabled}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        onChange={setValue}
        onKeyDown={onKeyDown}
        onInputChange={onInputChange}
        noResultsText={null}
        formatOptionLabel={(option: SerialsAutocompleteOption) => {
          const { serial, colorName, sizeName } = option;

          option.label = `${serial}${colorName ? ` Color: ${colorName}` : ''}${
            sizeName ? `, Size: ${sizeName}` : ''
          }`;

          return option;
        }}
        components={{
          DropdownIndicator: () => null,
        }}
        isDisabled={isDisabled}
        id="serials-autocomplete"
      />

      {hasSubmitFormButton ? (
        <button type="submit" ref={submitRef} style={{ display: 'none' }} />
      ) : null}

      {hiddenInput ? (
        <GuiDefHiddenInput {...hiddenInput} name="serialized_id" defaultValue={hiddenInputValue} />
      ) : null}
    </>
  );
};

export default SerialsAutocomplete;
